import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Button, Card, Col, Collapse, Empty, List, Modal, Row, Space, Typography } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import { SysLogDTO, getSysLogContent, getSysLogList } from '../../services/LogService';
import { KAPTAIN_URL } from '../../services/config';
import { LogViewer } from "@patternfly/react-log-viewer";

type ProgramLogProps = {};
type LogType = keyof SysLogDTO;

const LOG_TYPE_META: Array<{ type: LogType; title: string }> = [
  { type: 'sys', title: 'System log' },
  { type: 'app', title: 'App log' }
];

const SYSTEM_REALTIME_LOGS = new Set([
  'KaptainDB.log',
  'Kaptain SSE Service-nssm.log',
  'Commentary SSE Service-nssm.log',
  'Kaptain Server Service-nssm.log',
  'Commentary Server Service-nssm.log',
  'Kaptain Proxy Service-nssm.log'
]);

const getTodayDateParts = () => {
  const now = new Date();
  return {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate()
  };
};

const extractYmdDateNum = (filename: string) => {
  const ymdMatch = filename.match(/(20\d{2})[-_]?([01]\d)[-_]?([0-3]\d)/);
  if (!ymdMatch) {
    return -1;
  }

  return Number(`${ymdMatch[1]}${ymdMatch[2]}${ymdMatch[3]}`);
};

const extractDmyDateNum = (filename: string) => {
  const dmyMatch = filename.match(/([0-3]\d)-([01]\d)-(20\d{2})$/);
  if (!dmyMatch) {
    return -1;
  }

  return Number(`${dmyMatch[3]}${dmyMatch[2]}${dmyMatch[1]}`);
};

const normalizeLogFilenames = (type: LogType, filenames: string[] = []) => {
  return filenames
    .filter(filename => !filename.startsWith('.'))
    .sort((a, b) => {
      const dateA = type === 'app' ? extractDmyDateNum(a) : extractYmdDateNum(a);
      const dateB = type === 'app' ? extractDmyDateNum(b) : extractYmdDateNum(b);
      const dateDiff = dateB - dateA;
      return dateDiff !== 0 ? dateDiff : b.localeCompare(a);
    });
};

const isTodayLog = (type: LogType, filename: string, today: ReturnType<typeof getTodayDateParts>) => {
  if (type === 'sys') {
    return SYSTEM_REALTIME_LOGS.has(filename);
  }

  const dmyMatch = filename.match(/([0-3]\d)-([01]\d)-(20\d{2})$/);
  if (!dmyMatch) {
    return false;
  }

  return Number(dmyMatch[1]) === today.day && Number(dmyMatch[2]) === today.month && Number(dmyMatch[3]) === today.year;
};

export const ProgramLog: React.FC<ProgramLogProps> = () => {
  const [logs, setLogs] = useState<SysLogDTO>();
  const [logViewOpen, setLogViewOpen] = useState<boolean>(false);
  const [logContent, setLogContent] = useState<string>();

  useEffect(() => {
    getSysLogList().then(data => setLogs(data));
  }, []);

  const todayDate = useMemo(() => getTodayDateParts(), []);

  const groupedLogs = useMemo(() => {
    if (!logs) {
      return undefined;
    }

    return LOG_TYPE_META.reduce((acc, { type }) => {
      const normalized = normalizeLogFilenames(type, logs[type]);
      const today = normalized.filter(filename => isTodayLog(type, filename, todayDate));
      const history = normalized.filter(filename => !isTodayLog(type, filename, todayDate));

      return {
        ...acc,
        [type]: { today, history }
      };
    }, {} as Record<LogType, { today: string[]; history: string[] }>);
  }, [logs, todayDate]);

  const viewLog = async (type: LogType, filename: string) => {
    setLogViewOpen(true);
    setLogContent(undefined);

    const content = await getSysLogContent(type, filename);
    setLogContent(content);
  };

  const lineCount = useMemo(
    () => logContent?.split("\n").length ?? 0,
    [logContent]
  );

  const renderLogFileItem = (type: LogType, filename: string) => {
    return (
      <List.Item
        actions={[
          <Button key={`view-${type}-${filename}`} type="link" onClick={() => viewLog(type, filename)}>
            view
          </Button>,
          <Button
            key={`download-${type}-${filename}`}
            type="link"
            target="_blank"
            href={`${KAPTAIN_URL}/api/log/sys/${type}/${filename}/download`}
          >
            download
          </Button>
        ]}
      >
        <Space>
          <Avatar shape="square" icon={<FileTextOutlined />} />
          <Typography.Text>{filename}</Typography.Text>
        </Space>
      </List.Item>
    );
  };

  const renderLogList = (type: LogType, filenames: string[], emptyDesc: string) => {
    if (filenames.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDesc} />;
    }

    return <List size="small" dataSource={filenames} renderItem={filename => renderLogFileItem(type, filename)} />;
  };

  return (
    <React.Fragment>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Today logs" loading={!groupedLogs}>
            <Row gutter={[16, 16]}>
              {LOG_TYPE_META.map(({ type, title }) => (
                <Col key={`today-${type}`} xs={24} lg={12}>
                  <Typography.Title level={5}>{title}</Typography.Title>
                  {groupedLogs && renderLogList(type, groupedLogs[type].today, 'No log file generated today')}
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Card title="History logs" loading={!groupedLogs}>
            <Collapse
              ghost
              items={
                groupedLogs
                  ? LOG_TYPE_META.map(({ type, title }) => ({
                    key: `history-${type}`,
                    label: `${title} (${groupedLogs[type].history.length})`,
                    children: renderLogList(type, groupedLogs[type].history, 'No historical logs')
                  }))
                  : []
              }
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title="View log"
        open={logViewOpen}
        maskClosable={false}
        footer={null}
        width={'80vw'}
        loading={logContent === undefined}
        onCancel={() => {
          setLogViewOpen(false);
          setLogContent(undefined);
        }}
      >
        <LogViewer
          data={logContent}
          hasLineNumbers={false}
          scrollToRow={lineCount}
        />
      </Modal>
    </React.Fragment>
  );
};
